import {CounterBadge, makeStyles, tokens} from '@fluentui/react-components';
import {Building24Regular, Checkmark24Regular, Settings24Regular} from '@fluentui/react-icons';
import {useTranslation} from 'react-i18next';

import {useAuth} from '@app/providers/AuthProvider';
import {useOpenApprovals} from '@features/approvals/hooks/useApprovals';
import {APPROVING_ROLE} from '@shared/auth/roles';
import {EmployeeNavMenu} from './EmployeeNavMenu';
import {GateKeeperNavMenu} from './GateKeeperNavMenu';
import {NavLink} from 'react-router-dom';
import {AdminNavMenu} from "@shared/components/navigation/AdminNavMenu.tsx";

const useStyles = makeStyles({
    adminLink: {
        display: 'flex',
        flexDirection: 'column',
        padding: tokens.spacingVerticalM,
        gap: tokens.spacingVerticalXS,
    },
    section: {
        marginBottom: tokens.spacingVerticalM,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground3,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    },
    link: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        textDecoration: 'none',
        color: 'inherit',
        fontSize: tokens.fontSizeBase300,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    icon: {
        fontSize: '20px',
        flexShrink: 0,
    },
    navLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalS,
        width: '100%',
    },
});

export function NavMenu() {
    const styles = useStyles();
    const {t} = useTranslation('navigation');
    const {t: tApprovals} = useTranslation('approvals');
    const {hasRole} = useAuth();

    const isAdmin = hasRole('admin');
    const isGateKeeper = hasRole('gate_keeper');
    const isEmployee = hasRole('employee');
    const isContractorManager = hasRole('contractor_manager');
    const isApproving = hasRole(APPROVING_ROLE);

    // Phase 43 D-11/D-12 reverses Phase 25 plan 25-10's bare, unguarded block — that block read
    // "approver eligibility is a dynamic per-stage check, never a static role" (D-03/PD-20). The
    // single-stage, role-based model makes eligibility a real Keycloak role (`admin` or
    // `Approving`, plans 43-01/43-05), so this block is now guarded exactly like its siblings
    // above. Hiding the link is still cosmetic — the server's RequireRole(admin, Approving) on
    // every approval-workflow route is the real gate — but the query itself is ALSO gated
    // (`enabled: isAdmin || isApproving`) so a non-approver's browser never issues a request that
    // would 403 on first paint (RESEARCH Pitfall 8). The counter reuses the same useOpenApprovals()
    // cache entry the inbox page itself reads (one request, two consumers), so mounting nav+inbox
    // together never doubles the network call.
    const openStagesQuery = useOpenApprovals({ enabled: isAdmin || isApproving });
    const openStagesCount = openStagesQuery.data?.items.length ?? 0;
        return (
            <>
                { (isAdmin) && (
                    <AdminNavMenu/>
                )}

                {/* The admin menu already contains the employee and gate keeper entries. */}
                { (!isAdmin && isEmployee) && (
                    <EmployeeNavMenu/>
                )}

                { (!isAdmin && isGateKeeper) && (
                    <GateKeeperNavMenu showSharedItems={!isEmployee}/>
                )}

                { (isAdmin || isApproving) && (
                    <div className={styles.adminLink}>
                        <div className={styles.sectionTitle}>{tApprovals('nav.label')}</div>
                        <NavLink to="/approvals" className={styles.link}>
                            <span className={styles.icon}><Checkmark24Regular/></span>
                            <span className={styles.navLabel}>
                                <span>{tApprovals('nav.label')}</span>
                                <CounterBadge count={openStagesCount} color="danger" size="small"/>
                            </span>
                        </NavLink>
                    </div>
                )}

                { (isAdmin || isContractorManager) && (
                    <div className={styles.adminLink}>
                        <div className={styles.sectionTitle}>{t('contractors')}</div>
                        <NavLink to="/contractors" className={styles.link}>
                            <span className={styles.icon}><Building24Regular/></span>
                            <span>{t('contractors')}</span>
                        </NavLink>
                    </div>
                )}
                
                {isAdmin && (
                    <div className={styles.adminLink}>
                        <div className={styles.sectionTitle}>{t('administration')}</div>
                        <NavLink to="/admin" className={styles.link}>
                            <span className={styles.icon}><Settings24Regular/></span>
                            <span>{t('adminArea')}</span>
                        </NavLink>
                    </div>
                )}
            </>
        );
}
